
import { mdiCloseCircle } from "@mdi/js";
import IconModule from "@mdi/react";

import { interopDefault } from "../../../lib/interop";
import {
    useState,
    type ChangeEvent,
    type FormEvent,
    type MouseEvent,
} from "react";
import toast from "react-hot-toast";

import style from "./styles/ContactUs.module.scss";
import {
    errorMessage,
    useMeQuery,
    useSubmitFeedbackMutation,
} from "../../../api";
import { useAppSelector } from "../../../store/hooks";

/**
 * Normalised at the import: this package is CommonJS, and the production
 * bundler hands back its exports object rather than the component. See
 * src/lib/interop.ts.
 */
const Icon = interopDefault(IconModule);

type Prop = {
    setOpenModal: (state: boolean) => void;
};

/**
 * The contact form.
 *
 * `POST /feedback` accepts an optional bearer token, so this works signed out
 * on the landing page and attaches the account when someone is signed in. The
 * name and address are pre-filled from the session for the second case, which
 * is the only reason the profile query is here.
 */
const ContactUsModal = ({ setOpenModal }: Prop) => {
    const signedIn = useAppSelector((state) => Boolean(state.auth.accessToken));
    const { data: profile } = useMeQuery(undefined, { skip: !signedIn });
    const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();

    // What the person has typed. Empty means "not typed yet", and the value
    // shown falls back to the profile, so the fields are prefilled the moment
    // the profile arrives without an effect writing into state, and clearing a
    // field stays cleared.
    const [typed, setTyped] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });

    const profileName = profile
        ? [profile.firstName, profile.lastName].filter(Boolean).join(" ")
        : "";

    const clientInfo = {
        name: typed.name || profileName,
        email: typed.email || profile?.email || "",
        subject: typed.subject,
        message: typed.message,
    };

    const handleInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setTyped((previous) => ({ ...previous, [name]: value }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try {
            await submitFeedback(clientInfo).unwrap();
            toast.success("Message sent. We will be in touch.");
            setOpenModal(false);
        } catch (error) {
            toast.error(errorMessage(error, "Could not send your message."));
        }
    };

    const handleBgClick = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            setOpenModal(false);
        }
    };
    return (
        <>
            <div className={style.modal_bg} onClick={handleBgClick}>
                <div className={style.modal_box}>
                    <div>
                        <span
                            className={style.close}
                            onClick={() => setOpenModal(false)}
                        >
                            <Icon
                                path={mdiCloseCircle}
                                size={2}
                                title="close"
                            />
                        </span>
                        <h2>Contact Support</h2>
                    </div>
                    <form onSubmit={(e) => void handleSubmit(e)}>
                        <div className={style.form_cont}>
                            <div className={style.input_group}>
                                <label htmlFor="name">
                                    Name
                                </label>
                                <div className={style.input_cont}>
                                    <input
                                        name="name"
                                        type="text"
                                        id="name"
                                        value={clientInfo.name}
                                        required
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            <div className={style.input_group}>
                                <label htmlFor="email" className="label">
                                    Email
                                </label>
                                <div className={style.input_cont}>
                                    <input
                                        name="email"
                                        type="text"
                                        id="email"
                                        required
                                        onChange={handleInputChange}
                                        value={clientInfo.email}
                                    />
                                </div>
                            </div>

                            <div className={style.input_group}>
                                <label htmlFor="subject" className="label">
                                    Subject
                                </label>
                                <div className={style.input_cont}>
                                    <input
                                        name="subject"
                                        id="subject"
                                        required
                                        onChange={handleInputChange}
                                        value={clientInfo.subject}
                                    />
                                </div>
                            </div>

                            <div className={style.input_group}>
                                <label htmlFor="message">Message:</label>
                                <div className={style.text_area}>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={clientInfo.message}
                                        onChange={handleInputChange}
                                        className={style.text_area_input}
                                        rows={4}
                                        required
                                    />
                                </div>
                            </div>
                            <button className={style.contact_btn} disabled={isLoading}>
                                {isLoading ? "Sending…" : "Send"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default ContactUsModal;
